import React, { useState, useMemo, useRef } from 'react';

const SliderInput = ({ label, value, setValue, min, max, step, format, suffix }) => {
  const trackRef = useRef(null);
  const draggingRef = useRef(false);

  const handleStart = (clientX) => {
    draggingRef.current = true;
    
    if (trackRef.current) {
      const rect = trackRef.current.getBoundingClientRect();
      const pos = clientX - rect.left;
      const pct = Math.max(0, Math.min(1, pos / rect.width));
      let nv = min + pct * (max - min);
      nv = Math.round(nv / step) * step;
      nv = Math.max(min, Math.min(max, nv));
      setValue(nv);
    }
    
    const handleMove = (e) => {
      if (!draggingRef.current) return;
      const x = e.clientX || (e.touches && e.touches[0].clientX);
      if (x !== undefined) {
        if (!trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        const pos = x - rect.left;
        const pct = Math.max(0, Math.min(1, pos / rect.width));
        let nv = min + pct * (max - min);
        nv = Math.round(nv / step) * step;
        nv = Math.max(min, Math.min(max, nv));
        setValue(nv);
      }
    };

    const handleEnd = () => {
      draggingRef.current = false;
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove);
    document.addEventListener('touchend', handleEnd);
  };

  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <label style={{ fontSize: '14px', color: '#4A4A4A', fontWeight: '500' }}>{label}</label>
        <span style={{ fontSize: '18px', fontWeight: '700', color: '#4A2C7A' }}>
          {format ? format(value) : value}{suffix || ''}
        </span>
      </div>
      <div
        ref={trackRef}
        onMouseDown={(e) => { e.preventDefault(); handleStart(e.clientX); }}
        onTouchStart={(e) => { e.preventDefault(); handleStart(e.touches[0].clientX); }}
        style={{ position: 'relative', height: '28px', cursor: 'pointer', touchAction: 'none' }}
      >
        <div style={{ position: 'absolute', top: '10px', left: 0, right: 0, height: '8px', borderRadius: '4px', background: '#E0D6F0' }} />
        <div style={{ position: 'absolute', top: '10px', left: 0, width: percentage + '%', height: '8px', borderRadius: '4px', background: '#4A2C7A' }} />
        <div style={{ position: 'absolute', top: '2px', left: 'calc(' + percentage + '% - 12px)', width: '24px', height: '24px', borderRadius: '50%', background: '#4A2C7A', border: '3px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#888', marginTop: '4px' }}>
        <span>{format ? format(min) : min}{suffix || ''}</span>
        <span>{format ? format(max) : max}{suffix || ''}</span>
      </div>
    </div>
  );
};

export default function ArcoraROICalculatorPublic() {
  const [participants, setParticipants] = useState(50);
  const [avgSalary, setAvgSalary] = useState(110000);
  const [teamMemberSalary, setTeamMemberSalary] = useState(90000);
  const [percentLeaders, setPercentLeaders] = useState(70);
  const [avgTeamSize, setAvgTeamSize] = useState(5);
  const [turnoverRate, setTurnoverRate] = useState(12);

  const COACHING_COST_PER_PERSON = 600;
  const ENGAGEMENT_IMPROVEMENT_PP = 10.67;
  const PRODUCTIVITY_RATE_PER_10PP = 0.035;
  
  const ASSUMPTIONS = {
    turnoverReduction: 0.15,
    turnoverReductionTeam: 0.10,
    replacementCostMultiplier: 0.5,
    absenteeismDaysPerYear: 5,
    absenteeismReduction: 0.15,
    workingDaysPerYear: 250,
    presenteeismBaseline: 0.025,
    presenteeismReduction: 0.10,
    wellbeingParticipantRate: 0.87,
    teamProductivityLift: 0.005,
    overlapDiscount: 0.40,
    sessionScaling: 0.50,
  };

  const calculations = useMemo(() => {
    const totalInvestment = participants * COACHING_COST_PER_PERSON;
    const productivityGainPercent = (ENGAGEMENT_IMPROVEMENT_PP / 10) * PRODUCTIVITY_RATE_PER_10PP * 100;
    const engagementBenefitPerPerson = avgSalary * (productivityGainPercent / 100);
    const totalEngagementBenefit = engagementBenefitPerPerson * participants;
    
    const turnoverRateDecimal = turnoverRate / 100;
    const avoidedExitsParticipants = participants * turnoverRateDecimal * ASSUMPTIONS.turnoverReduction;
    const turnoverSavingsParticipants = avoidedExitsParticipants * avgSalary * ASSUMPTIONS.replacementCostMultiplier;
    
    const leadersCoached = participants * (percentLeaders / 100);
    const teamMembersAffected = leadersCoached * avgTeamSize;
    const avoidedExitsTeam = teamMembersAffected * turnoverRateDecimal * ASSUMPTIONS.turnoverReductionTeam;
    const turnoverSavingsTeam = avoidedExitsTeam * teamMemberSalary * ASSUMPTIONS.replacementCostMultiplier;
    
    const dailySalary = avgSalary / ASSUMPTIONS.workingDaysPerYear;
    const absenteeismDaysAvoided = participants * ASSUMPTIONS.absenteeismDaysPerYear * ASSUMPTIONS.absenteeismReduction;
    const absenteeismSavings = absenteeismDaysAvoided * dailySalary;
    
    const presenteeismSavings = participants * ASSUMPTIONS.wellbeingParticipantRate * avgSalary * ASSUMPTIONS.presenteeismBaseline * ASSUMPTIONS.presenteeismReduction;
    const teamBenefit = teamMembersAffected * teamMemberSalary * ASSUMPTIONS.teamProductivityLift;
    
    const totalAdditionalRaw = turnoverSavingsParticipants + turnoverSavingsTeam + absenteeismSavings + presenteeismSavings + teamBenefit;
    const additionalAfterOverlap = totalAdditionalRaw * (1 - ASSUMPTIONS.overlapDiscount);
    const additionalScaled = additionalAfterOverlap * ASSUMPTIONS.sessionScaling;
    
    const totalBenefit = totalEngagementBenefit + additionalScaled;
    const roi = totalBenefit / totalInvestment;
    
    return { totalInvestment, totalBenefit, roi };
  }, [participants, avgSalary, teamMemberSalary, percentLeaders, avgTeamSize, turnoverRate]);

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(num);
  };

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', background: 'linear-gradient(135deg, #F5F0E8 0%, #EDE6DC 100%)', minHeight: '100vh', padding: '40px 20px', color: '#2D1B4E' }}>
      <div style={{ maxWidth: '500px', margin: '0 auto' }}>
        
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#4A2C7A', margin: '0 0 8px 0' }}>Coaching ROI Calculator</h1>
          <p style={{ fontSize: '14px', color: '#4A4A4A', margin: 0 }}>Calculate your expected return on investment from Arcora's coaching program</p>
        </div>

        <div style={{ background: 'rgba(255, 255, 255, 0.7)', borderRadius: '16px', padding: '24px', marginBottom: '24px', boxShadow: '0 2px 8px rgba(74, 44, 122, 0.1)' }}>
          <SliderInput label="Number of Coaching Participants" value={participants} setValue={setParticipants} min={1} max={200} step={1} />
          <SliderInput label="Average Salary of Participants" value={avgSalary} setValue={setAvgSalary} min={40000} max={1000000} step={5000} format={formatCurrency} />

          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(74, 44, 122, 0.15)' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#4A2C7A' }}>Default Inputs</div>
              <div style={{ fontSize: '11px', color: '#666666' }}>Adjust to match your situation</div>
            </div>
            <SliderInput label="% People Leaders" value={percentLeaders} setValue={setPercentLeaders} min={0} max={100} step={1} suffix="%" />
            <SliderInput label="Avg Team Size" value={avgTeamSize} setValue={setAvgTeamSize} min={0} max={50} step={1} />
            <SliderInput label="Avg Team Member Salary" value={teamMemberSalary} setValue={setTeamMemberSalary} min={30000} max={1000000} step={5000} format={formatCurrency} />
            <SliderInput label="Turnover Rate" value={turnoverRate} setValue={setTurnoverRate} min={0} max={50} step={1} suffix="%" />
          </div>
        </div>

        <div style={{ background: 'rgba(74, 44, 122, 0.1)', borderRadius: '16px', padding: '32px', textAlign: 'center', border: '1px solid rgba(74, 44, 122, 0.2)' }}>
          <div style={{ fontSize: '14px', color: '#4A4A4A', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px' }}>Estimated ROI</div>
          <div style={{ fontSize: '64px', fontWeight: '800', color: '#4A2C7A', lineHeight: 1, marginBottom: '20px' }}>{calculations.roi.toFixed(0)}:1</div>
          <div style={{ background: 'rgba(74, 44, 122, 0.1)', borderRadius: '8px', padding: '12px 16px', fontSize: '12px', color: '#666666', lineHeight: '1.5' }}>
            <strong style={{ color: '#4A4A4A' }}>Based on:</strong> Engagement, turnover, absenteeism, well-being, and team effects
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: '24px' }}>
          <p style={{ fontSize: '13px', color: '#666666', marginBottom: '16px' }}>Want to see the full methodology and breakdown?</p>
          <button style={{ background: 'linear-gradient(135deg, #4A2C7A 0%, #3D2366 100%)', color: '#fff', border: 'none', borderRadius: '8px', padding: '14px 32px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}>Book a Call</button>
        </div>

        <div style={{ textAlign: 'center', marginTop: '32px', fontSize: '11px', color: '#666666' }}>
          <p style={{ margin: '0 0 4px 0' }}>Based on Arcora's base coaching program. Results will vary depending on number of sessions and price.</p>
          <p style={{ margin: 0 }}>All figures in Canadian Dollars (CAD). Annual ROI over 12-month benefit period.</p>
        </div>
      </div>
    </div>
  );
}
